import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  VStack,
  HStack,
  Input,
  Button,
  IconButton
} from '@chakra-ui/react';
import { MdSend, MdCameraAlt } from 'react-icons/md';

const ChatInput = ({ 
  budget, 
  inputMessage, 
  setInputMessage, 
  onSendMessage, 
  onFileUpload, 
  selectedImage, 
  onRemoveImage,
  isLoading = false
}) => {
  const fileInputRef = useRef();

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      onFileUpload(file);
    }
  };

  return (
    <Box className="bg-gradient-to-br from-stone-100 to-stone-200 border-t border-stone-300">
      

      {/* Input Controls */}
      <Box px={{ base: 6, md: 8 }} pt={2} pb={4}>
        <VStack spacing={4}>
          
          {/* Selected Image Preview */}
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="self-start"
            >
              <Box position="relative" display="inline-block">
                <img
                  src={selectedImage}
                  alt="Selected outfit"
                  style={{
                    maxWidth: '80px',
                    maxHeight: '80px',
                    borderRadius: '8px',
                    objectFit: 'cover'
                  }}
                />
                <IconButton
                  position="absolute"
                  top="-8px"
                  right="-8px"
                  size="sm"
                  colorScheme="red"
                  borderRadius="full"
                  onClick={onRemoveImage}
                  aria-label="Remove image"
                  fontSize="xs"
                >
                  ×
                </IconButton>
              </Box>
            </motion.div>
          )}

          {/* Text Input Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full"
          >
            <HStack spacing={3}>
              <Input
                flex={1}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="What are you looking for today?"
                className="bg-stone-50 border-stone-300 focus:border-rose-400 rounded-xl px-4 py-6 text-stone-800"
                fontSize="md"
                _focus={{
                  borderColor: 'rose.400',
                  boxShadow: '0 0 0 1px rgba(244, 63, 94, 0.4)'
                }}
              />
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <IconButton
                  onClick={handleFileUploadClick}
                  className="bg-gradient-to-r from-stone-500 to-stone-600 hover:from-stone-600 hover:to-stone-700 text-white rounded-lg transition-all duration-300"
                  minW="auto"
                  h="auto"
                  p={3}
                  aria-label="Upload photo"
                >
                  <MdCameraAlt size={18} />
                </IconButton>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  onClick={() => onSendMessage()}
                  isDisabled={!inputMessage.trim() && !selectedImage}
                  isLoading={isLoading}
                  className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white px-4 py-3 rounded-lg transition-all duration-300"
                  minW="auto"
                  h="auto"
                >
                  <MdSend size={18} />
                </Button>
              </motion.div>
            </HStack>
          </motion.div>


        </VStack>
      </Box>

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
      />
      
    </Box>
  );
};

export default ChatInput;